import React, { ComponentPropsWithRef, forwardRef, useId } from "react";
import { Input as GeneralInput } from "../ui/input";
import { useField, FormScope, ValueOfInputType } from "@rvf/remix";
import { useTranslation } from "react-i18next";
import { MandatoryLabel } from "./MandatoryLabel";
import { NumericFormat } from "react-number-format";

type BaseInputProps = Omit<ComponentPropsWithRef<"input">, "type">;

interface IInputProps<Type extends string> extends BaseInputProps {
  label?: string;
  id?: string;
  isMandatory?: boolean;
  scope: FormScope<ValueOfInputType<Type>>;
  type?: string;
  defaultValue?: string;
  pattern?: string;
  multiline?: boolean;
  isParkAction?: boolean;
  cols?: number;
}

interface INumericInputProps<Type extends string> extends BaseInputProps {
  label?: string;
  id?: string;
  isMandatory?: boolean;
  scope: FormScope<ValueOfInputType<Type>>;
  defaultValue?: string;
  decimalSeparator?: string;
  thousandSeparator?: string;
  isParkAction?: boolean;
}

type InputType = <Type extends string>(
  props: IInputProps<Type> | INumericInputProps<Type>
) => React.ReactNode;

const DefaultInput = forwardRef<HTMLInputElement, IInputProps<string>>(
  (
    { label, scope, id, isMandatory, name, multiline, defaultValue, isParkAction = false, cols = 50, ...rest },
    ref
  ) => {
    const field = useField(scope);
    const fieldName = name || field.name();
    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = useId();
    const { t } = useTranslation();
    return (
      <div className={"flex flex-col"}>
        <div className={"flex flex-col gap-1"}>
          {label && (
            <MandatoryLabel
              hidden={rest.type === "hidden"}
              inputId={inputId}
              label={label}
              isMandatory={isMandatory || false}
              isParkAction={isParkAction}
            />
          )}
          {multiline ? (
            <>
              <textarea
                onChange={(e) => field.onChange(e.target.value)}
                value={field.value() || defaultValue}
                name={fieldName}
                cols={cols}
                rows={5}
                className={"rounded-lg p-2 border h-10 pl-3"}
              />
            </>
          ) : (
            <GeneralInput
              className={"rounded-lg border h-10 pl-3"}
              name={fieldName}
              {...field.getInputProps({
                id: inputId,
                ref,
                "aria-describedby": errorId,
                "aria-invalid": !!field.error(),
                ...rest,
              })}
            />
          )}
        </div>
        {field.error() && rest.type !== "hidden" && !isParkAction && (
          <p id={errorId} className={"text-sm text-red-500"}>
            {t(field.error() || "")}
          </p>
        )}
      </div>
    );
  }
);
DefaultInput.displayName = "Input";

const NumericInput = forwardRef<HTMLInputElement, INumericInputProps<string>>(
  (
    {
      label,
      scope,
      isMandatory,
      name,
      thousandSeparator = ",",
      decimalSeparator = ".",
      isParkAction = false,
      ...rest
    },
    ref
  ) => {
    const field = useField(scope);
    const fieldName = name || field.name();
    const inputId = useId();
    const errorId = useId();
    const { t } = useTranslation();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      field.onChange(value);
    };

    return (
      <div className={"flex flex-col"}>
        <div className={"flex flex-col gap-1"}>
          {label && (
            <MandatoryLabel
              inputId={inputId}
              label={label}
              isMandatory={isMandatory || false}
              isParkAction={isParkAction}
            />
          )}

          <NumericFormat
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            decimalScale={2}
            fixedDecimalScale={true}
            // @ts-expect-error I can't get this to work
            value={(field.value() as number | string | null) ?? ""}
            name={fieldName}
            inputMode="decimal"
            onChange={handleChange}
            id={inputId}
            ref={ref}
            aria-describedby={errorId}
            aria-invalid={!!field.error()}
            {...rest}
            className={"rounded-lg border h-10 pl-3"}
          />
        </div>
        {field.error() && !isParkAction && (
          <p id={errorId} className={"text-sm text-red-500"}>
            {t(field.error() || "")}
          </p>
        )}
      </div>
    );
  }
);
NumericInput.displayName = "NumericInput";

export const FormInput = DefaultInput as InputType;
export const NumericFormInput = NumericInput as InputType;
